import React from 'react';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Image,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  documentId,
  getDoc,
  getFirestore,
  onSnapshot,
  query,
  runTransaction,
  where,
} from 'firebase/firestore';
import { MdFitnessCenter, MdOutlineAddCircleOutline } from 'react-icons/md';
import Card from 'components/card/Card';
import { AuthContext } from 'contexts/AuthContext';
import { app, functions } from 'lib/firebase';
import { isInstructorOnboardingComplete } from 'utils/instructorOnboarding';
import {
  areOpportunityFacilitiesLoaded,
  buildOpportunityMapItems,
  chunkClassIdsForFirestore,
  filterClaimableOpportunityClasses,
  getOpportunityHydrationState,
  getLatLngFromLocation,
  getNearbyOpportunityProfileIssue,
  normalizeNearbyClassIds,
  validateOpportunityClaim,
} from 'utils/instructorOpportunities';
import {
  buildClaimClassUpdate,
  formatClassDateTimeLA,
  getClassDisplayText,
  getClassDurationMinutes,
} from 'utils/instructorClassWorkflows';
import {
  buildClassActivityActor,
  buildInstructorClassClaimActivity,
} from 'utils/classActivityLogger';
import { getClassTypeIcon } from 'utils/classIcons';
import { ACCOUNT_STATUS_STRIKE_PAUSED } from 'utils/lateCancellationStrikes';
import ClassDetailsModal from 'views/admin/calendar/components/InstructorClassDetailsModal';
import ClaimSuccessAnimation from 'views/admin/opportunities/components/ClaimSuccessAnimation';
import OpportunityFinderMap from 'views/admin/opportunities/components/OpportunityFinderMap';
import InstructorStats from './InstructorStats';
import InstructorUpcomingClasses from './InstructorUpcomingClasses';
import InstructorNeedsAttentionClasses from './InstructorNeedsAttentionClasses';
import InstructorDashboardCalendarView from './InstructorDashboardCalendarView';

const db = getFirestore(app);

function OpportunitiesPreview() {
  const history = useHistory();
  const {
    user,
    userDocId,
    userData,
    stripeSetupComplete,
    stripeSetupRequired,
  } = React.useContext(AuthContext);
  const toast = useToast();
  const [opportunities, setOpportunities] = React.useState([]);
  const [opportunityClassIds, setOpportunityClassIds] = React.useState([]);
  const [facilitiesById, setFacilitiesById] = React.useState({});
  const [selectedOpportunityClass, setSelectedOpportunityClass] = React.useState(null);
  const [nearbyClassIdsLoaded, setNearbyClassIdsLoaded] = React.useState(false);
  const [isLoadingClassDocs, setIsLoadingClassDocs] = React.useState(false);
  const [isLoadingFacilities, setIsLoadingFacilities] = React.useState(false);
  const [error, setError] = React.useState('');
  const [claimingClassId, setClaimingClassId] = React.useState('');
  const [pendingClaimClass, setPendingClaimClass] = React.useState(null);
  const [claimedClassPreview, setClaimedClassPreview] = React.useState(null);
  const [showClaimSuccess, setShowClaimSuccess] = React.useState(false);
  const classDocsById = React.useRef({});
  const cancelClaimRef = React.useRef(null);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('gray.700', 'gray.400');
  const brandColor = useColorModeValue('brand.500', 'brand.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const instructorLocation = getLatLngFromLocation(userData?.location);
  const opportunityMapItems = buildOpportunityMapItems(opportunities, facilitiesById);
  const previewClasses = opportunities.slice(0, 2);
  const stripeComplete = Boolean(stripeSetupComplete);
  const opportunityHydration = getOpportunityHydrationState({
    nearbyClassIdsLoaded,
    isLoadingClassDocs,
    isLoadingFacilities: isLoadingFacilities || !areOpportunityFacilitiesLoaded(opportunities, facilitiesById),
    opportunities,
  });
  const instructorName = getClassDisplayText(
    [userData?.FirstName, userData?.LastName].filter(Boolean).join(' '),
    'Instructor Location'
  );

  React.useEffect(() => {
    let isActive = true;

    const loadDashboardOpportunities = async () => {
      if (!userDocId) {
        setOpportunityClassIds([]);
        setOpportunities([]);
        setNearbyClassIdsLoaded(true);
        setIsLoadingClassDocs(false);
        setIsLoadingFacilities(false);
        return;
      }

      setNearbyClassIdsLoaded(false);
      setIsLoadingClassDocs(false);
      setIsLoadingFacilities(false);

      const nearbyProfileIssue = getNearbyOpportunityProfileIssue(userData);
      if (nearbyProfileIssue) {
        setError(nearbyProfileIssue);
        setOpportunityClassIds([]);
        setOpportunities([]);
        setNearbyClassIdsLoaded(true);
        return;
      }

      setError('');

      try {
        const getNewNearbyClasses = httpsCallable(functions, 'getNewNearbyClasses');
        const result = await getNewNearbyClasses({ uid: userDocId });
        const classIds = normalizeNearbyClassIds(result?.data?.classes);

        if (isActive) {
          setOpportunityClassIds(classIds);
          setNearbyClassIdsLoaded(true);
        }
      } catch (loadError) {
        console.error('Error loading dashboard opportunities:', loadError);
        if (isActive) {
          setError('Unable to load nearby opportunities right now.');
          setNearbyClassIdsLoaded(true);
          setIsLoadingClassDocs(false);
          setIsLoadingFacilities(false);
        }
      }
    };

    loadDashboardOpportunities();

    return () => {
      isActive = false;
    };
  }, [userDocId, userData]);

  React.useEffect(() => {
    classDocsById.current = {};

    if (!nearbyClassIdsLoaded) {
      setOpportunities([]);
      setIsLoadingClassDocs(false);
      return undefined;
    }

    if (opportunityClassIds.length === 0) {
      setOpportunities([]);
      setIsLoadingClassDocs(false);
      return undefined;
    }

    setIsLoadingClassDocs(true);

    const publishClaimableClasses = () => {
      const claimableClasses = filterClaimableOpportunityClasses(Object.values(classDocsById.current))
        .sort((a, b) => Number(a.Date || 0) - Number(b.Date || 0));

      setOpportunities(claimableClasses);
      setIsLoadingClassDocs(false);
    };

    const unsubscribes = chunkClassIdsForFirestore(opportunityClassIds).map((idChunk) => {
      const unsubscribe = onSnapshot(query(
        collection(db, 'Classes'),
        where(documentId(), 'in', idChunk)
      ), (classSnapshot) => {
        idChunk.forEach((classId) => {
          delete classDocsById.current[classId];
        });

        classSnapshot.forEach((classDoc) => {
          classDocsById.current[classDoc.id] = { id: classDoc.id, ...classDoc.data() };
        });

        publishClaimableClasses();
      }, (snapshotError) => {
        console.error('Error listening to dashboard opportunities:', snapshotError);
        setError('Unable to keep nearby opportunities updated.');
        setIsLoadingClassDocs(false);
      });

      return unsubscribe;
    });

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [nearbyClassIdsLoaded, opportunityClassIds]);

  React.useEffect(() => {
    if (!selectedOpportunityClass) return;

    const updatedClass = opportunities.find((classData) => classData.id === selectedOpportunityClass.id);
    if (updatedClass) {
      setSelectedOpportunityClass(updatedClass);
    } else if (selectedOpportunityClass.AssignedInstructorID !== userDocId) {
      setSelectedOpportunityClass(null);
    }
  }, [opportunities, selectedOpportunityClass, userDocId]);

  React.useEffect(() => {
    if (!showClaimSuccess) return undefined;

    const timeoutId = setTimeout(() => setShowClaimSuccess(false), 1800);
    return () => clearTimeout(timeoutId);
  }, [showClaimSuccess]);

  React.useEffect(() => {
    let isActive = true;
    const missingFacilityIds = Array.from(new Set(
      opportunities
        .map((classData) => classData.FacilityID)
        .filter((facilityId) => facilityId && !facilitiesById[facilityId])
    ));

    if (opportunities.length === 0 || missingFacilityIds.length === 0) {
      setIsLoadingFacilities(false);
      return undefined;
    }

    const loadFacilities = async () => {
      setIsLoadingFacilities(true);
      const entries = await Promise.all(missingFacilityIds.map(async (facilityId) => {
        const facilityDoc = await getDoc(doc(db, 'Facilities', facilityId));
        return [facilityId, facilityDoc.exists() ? facilityDoc.data() : null];
      }));

      if (!isActive) return;
      setFacilitiesById((current) => ({
        ...current,
        ...Object.fromEntries(entries),
      }));
      setIsLoadingFacilities(false);
    };

    loadFacilities();

    return () => {
      isActive = false;
    };
  }, [opportunities, facilitiesById]);

  const openClaimConfirmation = (event, classData) => {
    event.stopPropagation();
    setPendingClaimClass(classData);
  };

  const closeClaimConfirmation = () => {
    if (!claimingClassId) setPendingClaimClass(null);
  };

  const handleClaimOpportunity = async () => {
    const classId = pendingClaimClass?.id;
    if (!stripeComplete || !userDocId) return;
    if (!classId) return;

    setClaimingClassId(classId);
    try {
      const actor = buildClassActivityActor({ user, userDocId, userData });
      let claimedClassData = null;

      await runTransaction(db, async (transaction) => {
        const classRef = doc(db, 'Classes', classId);
        const activityRef = doc(collection(db, 'Classes', classId, 'Activity'));
        const classSnapshot = await transaction.get(classRef);

        if (!classSnapshot.exists()) throw new Error('Class no longer exists.');
        const classSnapshotData = classSnapshot.data();
        claimedClassData = {
          id: classId,
          ...pendingClaimClass,
          ...classSnapshotData,
          AssignedInstructorID: userDocId,
        };

        validateOpportunityClaim({
          classData: classSnapshotData,
          stripeComplete,
          instructorAccountStatus: userData?.AccountStatus,
        });

        transaction.update(classRef, buildClaimClassUpdate(userDocId));
        transaction.set(activityRef, buildInstructorClassClaimActivity({
          actor,
          instructorId: userDocId,
          classId,
          classData: classSnapshotData,
        }));
      });

      setOpportunities((current) => current.filter((classData) => classData.id !== classId));
      setClaimedClassPreview(claimedClassData);
      setSelectedOpportunityClass(claimedClassData);
      setShowClaimSuccess(true);
      toast({
        title: 'Class claimed',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      setPendingClaimClass(null);
    } catch (claimError) {
      toast({
        title: 'Unable to claim class',
        description: claimError.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setClaimingClassId('');
    }
  };

  return (
    <Card id="opportunities-widget" p="20px" align="center" direction="column" w="100%">
      <Flex alignItems="center" w="100%" mb="30px" justify="space-between">
        <Flex alignItems="center">
          <Icon as={MdOutlineAddCircleOutline} color={brandColor} w="28px" h="28px" me="12px" />
          <Text color={textColor} fontSize="xl" fontWeight="700" lineHeight="100%">
            Nearby Opportunities
          </Text>
        </Flex>
        <Button
          size="sm"
          variant="ghost"
          colorScheme="brand"
          onClick={() => history.push('/admin/opportunities')}
        >
          View All
        </Button>
      </Flex>

      <Text color={textColorSecondary} fontSize="sm" w="100%" mb="14px">
        Browse nearby classes in your travel area.
      </Text>

      {error && (
        <Alert status="warning" borderRadius="12px" mb="14px">
          <AlertIcon />
          <AlertDescription fontSize="sm">{error}</AlertDescription>
        </Alert>
      )}

      <Box
        data-testid="dashboard-opportunities-map"
        w="100%"
        h="230px"
        borderRadius="14px"
        overflow="hidden"
        border="1px solid"
        borderColor={borderColor}
        mb="14px"
      >
        <OpportunityFinderMap
          instructorLocation={instructorLocation}
          instructorName={instructorName}
          opportunities={opportunityMapItems}
          isLoading={opportunityHydration.isHydrating}
          selectedClassId={selectedOpportunityClass?.id || ''}
          onOpportunityClick={(classData) => setSelectedOpportunityClass(classData)}
          locationBadgeLabel="Your Location"
          defaultZoom={9}
          focusZoom={9}
          showRadiusControls={false}
          showSearchRadius={false}
          initialSearchRadius={25}
        />
      </Box>

      {opportunityHydration.isHydrating ? (
        <Flex align="center" justify="center" w="100%" py="18px" gap="10px">
          <Spinner color={brandColor} size="sm" />
          <Text color={textColorSecondary} fontSize="sm" fontWeight="600">
            Loading nearby opportunities...
          </Text>
        </Flex>
      ) : previewClasses.length > 0 ? (
        <Flex direction="column" w="100%" gap="10px">
          {previewClasses.map((classData) => {
            const facility = facilitiesById[classData.FacilityID] || {};
            const facilityName = getClassDisplayText(
              facility.FacilityName || facility.FirstName || classData.FacilityName,
              'Facility'
            );
                const className = getClassDisplayText(
                  classData.ClassName || classData.ClassType,
                  'Class Opportunity'
                );
                const classIcon = getClassTypeIcon(classData.ClassName || classData.ClassType);

                return (
              <Flex
                key={classData.id}
                data-testid={`dashboard-opportunity-card-${classData.id}`}
                w="100%"
                p="12px"
                border="1px solid"
                borderColor={borderColor}
                borderRadius="12px"
                align="center"
                justify="space-between"
                gap="12px"
                cursor="pointer"
                _hover={{ borderColor: brandColor, boxShadow: 'sm' }}
                onClick={() => setSelectedOpportunityClass(classData)}
              >
                <Flex align="center" gap="10px" minW="0" flex="1">
                  <Flex
                    data-testid="dashboard-opportunity-class-icon"
                    w="34px"
                    h="34px"
                    borderRadius="10px"
                    bg={brandColor}
                    align="center"
                    justify="center"
                    flexShrink="0"
                  >
                    {classIcon ? (
                      <Image
                        src={classIcon}
                        alt={className}
                        w="22px"
                        h="22px"
                        objectFit="contain"
                        filter="brightness(0) invert(1)"
                      />
                    ) : (
                      <Icon as={MdFitnessCenter} color="white" w="18px" h="18px" />
                    )}
                  </Flex>
                  <Box minW="0">
                    <Text color={textColor} fontSize="sm" fontWeight="800" noOfLines={1}>
                      {className}
                    </Text>
                    <Text color={textColorSecondary} fontSize="xs" mt="2px" noOfLines={1}>
                      {facilityName} • {formatClassDateTimeLA(classData.Date)}
                    </Text>
                  </Box>
                </Flex>
                <Flex align="center" gap="8px" flexShrink="0">
                  <Badge colorScheme="brand" borderRadius="8px">
                    {getClassDurationMinutes(classData)} min
                  </Badge>
                      <Button
                        size="xs"
                        colorScheme="brand"
                        onClick={(event) => openClaimConfirmation(event, classData)}
                        isLoading={claimingClassId === classData.id}
                        isDisabled={stripeSetupRequired}
                  >
                    Claim Class
                  </Button>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      ) : opportunityHydration.showEmptyState ? (
        <Flex direction="column" align="center" justify="center" w="100%" py="18px">
          <Text color={textColorSecondary} fontSize="sm" fontWeight="600" textAlign="center">
            No nearby opportunities right now.
          </Text>
        </Flex>
      ) : null}

      <Button
        mt="16px"
        w="100%"
        colorScheme="brand"
        onClick={() => history.push('/admin/opportunities')}
      >
        Find Opportunities
      </Button>

          <ClassDetailsModal
            isOpen={Boolean(selectedOpportunityClass)}
            onClose={() => setSelectedOpportunityClass(null)}
            classData={selectedOpportunityClass}
            facility={selectedOpportunityClass ? facilitiesById[selectedOpportunityClass.FacilityID] : null}
            onClaimClass={() => setPendingClaimClass(selectedOpportunityClass)}
            isClaiming={claimingClassId === selectedOpportunityClass?.id}
            canClaim={stripeComplete && userData?.AccountStatus !== ACCOUNT_STATUS_STRIKE_PAUSED}
          />

          <ClaimSuccessAnimation
            isVisible={showClaimSuccess}
            classData={claimedClassPreview}
          />

          <AlertDialog
            isOpen={Boolean(pendingClaimClass)}
            leastDestructiveRef={cancelClaimRef}
            onClose={closeClaimConfirmation}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="800">
                  Claim this class?
                </AlertDialogHeader>
                <AlertDialogBody>
                  <Text color={textColorSecondary} fontSize="sm">
                    This will assign the class to you and remove it from nearby opportunities for other instructors.
                  </Text>
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelClaimRef} onClick={closeClaimConfirmation} isDisabled={Boolean(claimingClassId)}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme="brand"
                    onClick={handleClaimOpportunity}
                    isLoading={claimingClassId === pendingClaimClass?.id}
                    ml={3}
                  >
                    Claim Class
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </Card>
      );
    }

export default function InstructorDashboard() {
  const { userData } = React.useContext(AuthContext);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('gray.700', 'gray.400');

  const onboardingComplete = isInstructorOnboardingComplete(userData);

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="instructor-dashboard">
      {!onboardingComplete && (
        <Card p="22px" mb="20px" border="1px solid" borderColor="orange.200">
          <Flex align={{ base: 'flex-start', md: 'center' }} justify="space-between" direction={{ base: 'column', md: 'row' }} gap="12px">
            <Box>
              <Text color={textColor} fontSize="lg" fontWeight="800">
                Complete instructor onboarding
              </Text>
              <Text color={textColorSecondary} fontSize="sm" mt="6px">
                Finish your profile, agreement, and payout setup before claiming new opportunities.
              </Text>
            </Box>
            <Badge colorScheme="orange" borderRadius="8px" px="10px">
              Incomplete
            </Badge>
          </Flex>
        </Card>
      )}

      <InstructorStats />

      <Box id="needs-attention-widget" mb="20px">
        <InstructorNeedsAttentionClasses />
      </Box>

      <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap="20px" mb="20px">
        <Box id="upcoming-classes-widget">
          <InstructorUpcomingClasses />
        </Box>
        <Box id="opportunities-widget">
          <OpportunitiesPreview />
        </Box>
      </SimpleGrid>

      <Box id="calendar-view-widget" mb="20px">
        <InstructorDashboardCalendarView />
      </Box>
    </Box>
  );
}
